/**
 * Traceur Plausible — initialisation et réécriture des adresses (SAAS-1125).
 *
 * @remarks
 * Deux routes publiques portent un jeton dans le chemin (`/cv/:token`, sa vue
 * d'analyse, et les résultats d'une tentative). Plausible retire la chaîne de
 * requête mais conserve le chemin : sans réécriture, le jeton serait stocké puis
 * affiché dans « Top Pages ». `rewritePayload` est le seul rempart — s'il tombe,
 * c'est une violation de données personnelles, pas un défaut d'affichage.
 *
 * `transformRequest` s'applique à TOUTES les requêtes (pages vues et événements
 * d'engagement) : il n'y a pas d'autre point d'accroche à couvrir.
 */

import { init, type PlausibleRequestPayload } from '@plausible-analytics/tracker';
import { matchRoutes } from 'react-router-dom';

type Routes = Parameters<typeof matchRoutes>[0];

/**
 * 404 normal : la dernière route correspondante est le `*` nu. Compteur de
 * liens cassés, il est censé apparaître.
 */
const NOT_FOUND_PATH = '/_notfound';

/**
 * Aucune route ne correspond — impossible avec l'arbre réel, qui se termine par
 * un `*`. Si cette ligne apparaît dans le tableau de bord, l'arbre a changé :
 * c'est l'alarme, ne pas la confondre avec `NOT_FOUND_PATH`.
 */
const UNMATCHED_PATH = '/_unmatched';

/** Modèle de route (`/cv/:token`) d'un chemin réel, jamais sa valeur. */
function routeTemplate(routes: Routes, pathname: string): string {
  const matches = matchRoutes(routes, pathname);
  if (!matches) return UNMATCHED_PATH;
  if (matches[matches.length - 1]?.route.path === '*') return NOT_FOUND_PATH;

  let template = '';
  for (const { route } of matches) {
    if (!route.path) continue;
    template = route.path.startsWith('/') ? route.path : `${template}/${route.path}`;
  }
  return template.replace(/\/{2,}/g, '/') || '/';
}

/** Domaine seul du référent — `undefined` si absent ou illisible. */
function referrerDomain(referrer: string | null | undefined): string | undefined {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).hostname || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Réécrit la charge utile avant envoi :
 *  - `u` : origine + modèle de route, sans requête ni fragment ;
 *  - `r` : domaine seul, ou retiré s'il est illisible ;
 *  - `p` : retiré — une propriété identifiante ferait tomber l'exemption de consentement.
 *
 * Renvoie `null` (événement abandonné) si `u` n'est pas une URL.
 */
export function rewritePayload(
  payload: PlausibleRequestPayload,
  routes: Routes,
): PlausibleRequestPayload | null {
  let url: URL;
  try {
    url = new URL(payload.u);
  } catch {
    return null;
  }

  const rewritten: PlausibleRequestPayload = {
    ...payload,
    u: `${url.origin}${routeTemplate(routes, url.pathname)}`,
  };
  delete rewritten.p;
  delete rewritten.r;

  const domain = referrerDomain(payload.r);
  if (domain !== undefined) rewritten.r = domain;
  return rewritten;
}

/** `init` ne peut être appelé qu'une fois ; le mode strict de React double les effets en dev. */
let initialized = false;

/**
 * Démarre le traceur. Sans `VITE_PLAUSIBLE_DOMAIN` ou `VITE_PLAUSIBLE_ENDPOINT`
 * (absents ou vides), ne fait rien : c'est le cas en local. Staging et production
 * les renseignent toutes les deux (build-staging et build-prod, via la CI) : la
 * mesure y est active.
 *
 * `endpoint` est obligatoire : son défaut est le service hébergé par l'éditeur.
 * Les autres options gardent leurs défauts (liens sortants, téléchargements et
 * formulaires éteints) ; pas de `customProperties`.
 */
export function initPlausible(routes: Routes): void {
  const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
  const endpoint = import.meta.env.VITE_PLAUSIBLE_ENDPOINT;
  if (!domain || !endpoint || initialized) return;

  initialized = true;
  init({
    domain,
    endpoint,
    transformRequest: (payload) => rewritePayload(payload, routes),
  });
}
